import { useCallback, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import PropTypes from "prop-types";

/**
 * ScrollToBottomButton - Shows a down arrow when user is not at bottom
 * Positioned above the right side of the text input window
 */
export function ScrollToBottomButton({ containerRef, scrollToBottom }) {
  const [showButton, setShowButton] = useState(false);

  // Find the scrollable element (the div with overflow-y-auto)
  const findScrollableElement = useCallback(() => {
    const container = containerRef?.current;
    if (!container) return null;
    // The scrollable element is typically the first child with overflow-y-auto
    const scrollable = container.querySelector('[class*="overflow-y-auto"]');
    return scrollable || container;
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const scrollableElement = findScrollableElement();
    if (!scrollableElement) return;

    const checkScrollPosition = () => {
      const element = findScrollableElement();
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      // Check if user is near the bottom (within 100px threshold)
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowButton(!isNearBottom);
    };

    // Check on mount
    checkScrollPosition();

    // Check on scroll
    scrollableElement.addEventListener("scroll", checkScrollPosition);

    // Check on resize (messages might change height)
    window.addEventListener("resize", checkScrollPosition);

    // Check when messages might update (using MutationObserver)
    const observer = new MutationObserver(checkScrollPosition);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      scrollableElement.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
      observer.disconnect();
    };
  }, [containerRef, findScrollableElement]);

  // Handle scroll to bottom - scroll the actual scrollable element
  const handleScrollToBottom = () => {
    const scrollableElement = findScrollableElement();
    if (scrollableElement) {
      scrollableElement.scrollTo({
        top: scrollableElement.scrollHeight,
        behavior: "smooth",
      });
    } else if (scrollToBottom) {
      // Fallback to the provided scrollToBottom function
      scrollToBottom();
    }
  };

  if (!showButton) return null;

  return (
    <button
      onClick={handleScrollToBottom}
      className={cn(
        "absolute -top-10 right-4 sm:right-6",
        "h-8 w-8 rounded-full",
        "inline-flex items-center justify-center",
        "border border-border",
        "shadow-md hover:shadow-lg",
        "transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        "z-40",
        // Light mode: darker than sidebar (96% -> 94% lightness), hover darker still (92%)
        "bg-[hsl(210_20%_94%)] hover:bg-[hsl(210_20%_92%)]",
        // Dark mode: lighter than sidebar (15% -> 12% lightness), hover lighter still (18%)
        "dark:bg-[hsl(220_4%_12%)] dark:hover:bg-[hsl(220_4%_18%)]",
        // Text color to match foreground
        "text-foreground"
      )}
      aria-label="Scroll to bottom"
    >
      <ChevronDown className="h-4 w-4" />
    </button>
  );
}

ScrollToBottomButton.propTypes = {
  containerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  scrollToBottom: PropTypes.func,
};
